import { Activity, useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

// third party (real inbox data — same service the Notifications pages use)
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

// material-ui
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import CardActions from '@mui/material/CardActions';
import Chip from '@mui/material/Chip';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

// project imports
import MainCard from 'ui-component/cards/MainCard';
import Transitions from 'ui-component/extended/Transitions';
import NotificationList from './NotificationList';

// zercode packages + app services (berry→lib seam, same as ProfileSection)
import { inboxListSchema, inboxMarkAllReadSchema, inboxUnreadCountSchema } from '@zercode/api-client';
import { useInboxStream } from '@zercode/auth';
import { notificationClient, NOTIFICATION_URL } from '../../../../../lib/zercode';

// assets
import { IconBell } from '@tabler/icons-react';

// notification status filter (only the values the inbox API actually supports)
const status = [
  {
    value: 'all',
    label: 'All'
  },
  {
    value: 'unread',
    label: 'Unread'
  }
];

// ==============================|| NOTIFICATION ||============================== //

export default function NotificationSection() {
  const theme = useTheme();
  const downMD = useMediaQuery(theme.breakpoints.down('md'));

  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('all');
  const onlyUnread = value === 'unread';

  const queryClient = useQueryClient();

  // unread badge count — polled as a safety net; SSE (below) is the live path
  const unreadQuery = useQuery({
    queryKey: ['notif-bell-unread'],
    queryFn: async () => {
      const { data } = await notificationClient.get<unknown>('/api/v1/notifications/inbox/unread-count');
      return inboxUnreadCountSchema.parse(data).unread;
    },
    refetchInterval: 120_000
  });

  // list — only fetched while the panel is open
  const inboxQuery = useQuery({
    queryKey: ['notif-bell-inbox', onlyUnread],
    enabled: open,
    queryFn: async () => {
      const { data } = await notificationClient.get<unknown>('/api/v1/notifications/inbox', {
        query: { page: 1, pageSize: 8, onlyUnread: onlyUnread || undefined }
      });
      return inboxListSchema.parse(data);
    }
  });

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['notif-bell-unread'] });
    void queryClient.invalidateQueries({ queryKey: ['notif-bell-inbox'] });
  }, [queryClient]);

  // live inbox updates over SSE (same helper the student InboxBell uses)
  useInboxStream(NOTIFICATION_URL, invalidate);

  const markRead = useMutation({
    mutationFn: async (id: string) => {
      await notificationClient.post<unknown>(`/api/v1/notifications/inbox/${id}/read`, {});
    },
    onSuccess: invalidate
  });

  const markAllRead = useMutation({
    mutationFn: async () => {
      const { data } = await notificationClient.post<unknown>('/api/v1/notifications/inbox/read-all', {});
      return inboxMarkAllReadSchema.parse(data);
    },
    onSuccess: invalidate
  });

  const unreadCount = unreadQuery.data ?? 0;
  const items = inboxQuery.data?.items ?? [];

  /**
   * anchorRef is used on different componets and specifying one type leads to other components throwing an error
   * */
  const anchorRef = useRef<any>(null);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: React.MouseEvent<HTMLDivElement> | MouseEvent | TouchEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const prevOpen = useRef(open);
  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current.focus();
    }
    prevOpen.current = open;
  }, [open]);

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement> | undefined) => {
    event?.target.value && setValue(event?.target.value);
  };

  return (
    <>
      <Box sx={{ ml: 2 }}>
        <Avatar
          variant="rounded"
          sx={{
            ...theme.typography.commonAvatar,
            ...theme.typography.mediumAvatar,
            transition: 'all .2s ease-in-out',
            color: theme.vars.palette.warning.dark,
            background: theme.vars.palette.warning.light,
            '&:hover, &[aria-controls="menu-list-grow"]': {
              color: theme.vars.palette.warning.light,
              background: theme.vars.palette.warning.dark
            },
            ...theme.applyStyles('dark', {
              color: theme.vars.palette.warning.dark,
              background: theme.vars.palette.dark.main,
              '&:hover, &[aria-controls="menu-list-grow"]': {
                color: theme.vars.palette.grey[800],
                background: theme.vars.palette.warning.dark
              }
            })
          }}
          ref={anchorRef}
          aria-controls={open ? 'menu-list-grow' : undefined}
          aria-haspopup="true"
          onClick={handleToggle}
        >
          <IconBell stroke={1.5} size="20px" />
        </Avatar>
      </Box>
      {/* ZERCODE CHANGE [STD-FE] — HEADER-MENU-PHONE: the same screen-edge clip the profile menu had,
          measured separately rather than assumed. The card below is capped at 330px, which is 10px wider
          than a 320px phone: the panel sat flush against the right edge and lost 10px off the end. The
          cap makes it fit and `preventOverflow` gives it a real 16px gutter instead of flush. */}
      <Popper
        placement={downMD ? 'bottom' : 'bottom-end'}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        sx={{ maxWidth: 'calc(100vw - 32px)' }}
        modifiers={[
          { name: 'offset', options: { offset: [downMD ? 5 : 0, 20] } },
          { name: 'preventOverflow', options: { padding: 16 } }
        ]}
      >
        {({ TransitionProps }) => (
          <ClickAwayListener onClickAway={handleClose}>
            <Transitions position={downMD ? 'top' : 'top-right'} in={open} {...TransitionProps}>
              <Paper>
                <Activity mode={open ? 'visible' : 'hidden'}>
                  <MainCard border={false} elevation={16} content={false} boxShadow shadow={theme.shadows[16]} sx={{ maxWidth: 330 }}>
                    <Stack sx={{ gap: 2 }}>
                      <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', pt: 2, px: 2 }}>
                        <Stack direction="row" sx={{ gap: 2 }}>
                          <Typography variant="subtitle1">All Notification</Typography>
                          <Activity mode={unreadCount > 0 ? 'visible' : 'hidden'}>
                            <Chip
                              size="small"
                              label={String(unreadCount)}
                              variant="filled"
                              sx={{ color: 'background.default', bgcolor: 'warning.dark' }}
                            />
                          </Activity>
                        </Stack>
                        <Activity mode={unreadCount > 0 ? 'visible' : 'hidden'}>
                          <Typography
                            variant="subtitle2"
                            onClick={() => !markAllRead.isPending && markAllRead.mutate()}
                            sx={{ color: 'primary.main', cursor: 'pointer' }}
                          >
                            Mark as all read
                          </Typography>
                        </Activity>
                      </Stack>
                      <Box sx={{ height: 1, maxHeight: 'calc(100vh - 205px)', overflowX: 'hidden', '&::-webkit-scrollbar': { width: 5 } }}>
                        <Box sx={{ px: 2, pt: 0.25 }}>
                          <TextField
                            id="outlined-select-currency-native"
                            select
                            fullWidth
                            value={value}
                            onChange={handleChange}
                            slotProps={{ select: { native: true } }}
                          >
                            {status.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </TextField>
                        </Box>
                        <Divider sx={{ mt: 2 }} />
                        <NotificationList
                          items={items}
                          loading={inboxQuery.isPending}
                          onMarkRead={(id) => markRead.mutate(id)}
                          markingId={markRead.isPending ? (markRead.variables ?? null) : null}
                        />
                      </Box>
                    </Stack>
                    <CardActions sx={{ p: 1.25, justifyContent: 'center' }}>
                      <Button size="small" disableElevation component={Link} to="/notifications/inbox" onClick={() => setOpen(false)}>
                        View All
                      </Button>
                    </CardActions>
                  </MainCard>
                </Activity>
              </Paper>
            </Transitions>
          </ClickAwayListener>
        )}
      </Popper>
    </>
  );
}
